
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { createTransactionHash } from "../utils/transactionHash";

export const useTransactionDuplicateCheck = () => {
  const [lastTransactionId, setLastTransactionId] = useState<string | null>(null);
  const [lastTransactionHash, setLastTransactionHash] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [processingTransactions, setProcessingTransactions] = useState<Set<string>>(new Set());

  // Cache the last successful transaction submitted
  const setLastTransaction = (id: string, data: any) => {
    setLastTransactionId(id);
    // Add timestamp to the hash to track when it was created
    const hash = createTransactionHash(data) + `timestamp:${Date.now()}`;
    setLastTransactionHash(hash);
    
    // Store in localStorage as well for persistence between page refreshes
    try {
      localStorage.setItem('lastTransactionHash', hash);
      localStorage.setItem('lastTransactionId', id);
    } catch (e) {
      console.error('Failed to store transaction in localStorage:', e);
    }
  };

  const checkDuplicate = async (transactionData: any) => {
    // Prevent concurrent checks
    if (isChecking) {
      console.log('Duplicate check already in progress, preventing concurrent check');
      return { success: false, duplicateDetected: true };
    }
    
    try {
      setIsChecking(true);
      
      // Create a hash to identify potential duplicates
      const currentHash = createTransactionHash(transactionData);
      
      // Check if we have this transaction hash in our processing set
      if (processingTransactions.has(currentHash)) {
        console.warn('Transaction is currently being processed');
        toast.warning("Esta transação já está sendo processada");
        return { success: false, duplicateDetected: true };
      }

      // Check browser storage for recent submissions across page refreshes
      const storedHash = localStorage.getItem('lastTransactionHash');
      if (storedHash) {
        const hashParts = storedHash.split('timestamp:');
        const baseHash = hashParts[0];
        const timestamp = parseInt(hashParts[1] || '0');
        
        if (baseHash === currentHash) {
          const timeSinceLastSubmit = Date.now() - timestamp;
          if (timeSinceLastSubmit < 15000) { // 15 seconds threshold
            console.warn('Duplicate transaction detected via localStorage hash match');
            toast.warning("Uma transação idêntica foi registrada recentemente");
            return { success: false, duplicateDetected: true };
          }
        }
      }
      
      // Check if this is a duplicate based on the transaction hash in memory
      if (lastTransactionHash) {
        const hashParts = lastTransactionHash.split('timestamp:');
        const baseHash = hashParts[0];
        const timestamp = parseInt(hashParts[1] || '0');
        
        if (baseHash === currentHash) {
          const timeSinceLastSubmit = Date.now() - timestamp;
          if (timeSinceLastSubmit < 15000) { // 15 seconds threshold
            console.warn('Duplicate transaction detected via hash match');
            toast.warning("Transação idêntica enviada recentemente. Evitando duplicação.");
            return { success: false, duplicateDetected: true };
          }
        }
      }
      
      // Check for existing transactions with the same data in the last 45 seconds
      const fortyFiveSecondsAgo = new Date();
      fortyFiveSecondsAgo.setSeconds(fortyFiveSecondsAgo.getSeconds() - 45);
      
      const { data: existingTransactions, error: checkError } = await supabase
        .from('transactions')
        .select('id, created_at')
        .eq('type', transactionData.type)
        .eq('amount', transactionData.amount)
        .gte('created_at', fortyFiveSecondsAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(5);
        
      if (checkError) {
        console.error("Erro ao verificar transações existentes:", checkError);
      } else if (existingTransactions && existingTransactions.length > 0) {
        // Found potentially duplicate transactions created in the last 45 seconds
        console.warn('Potential duplicate transactions found in database:', existingTransactions.length);
        toast.warning("Uma transação similar foi registrada recentemente. Verifique sua lista de transações.");
        
        return { success: false, potentialDuplicate: true };
      }

      // Mark this transaction as being processed
      const newProcessingSet = new Set(processingTransactions);
      newProcessingSet.add(currentHash);
      setProcessingTransactions(newProcessingSet);
      
      // Automatically remove from processing after timeout
      setTimeout(() => {
        const updatedSet = new Set(processingTransactions);
        updatedSet.delete(currentHash);
        setProcessingTransactions(updatedSet);
      }, 30000);

      return { success: true };
    } finally {
      // Release the checking flag after a delay to prevent rapid retries
      setTimeout(() => setIsChecking(false), 2000);
    }
  };

  return { checkDuplicate, setLastTransaction };
};
